import type { SelectHTMLAttributes } from 'react';
import FieldFrame, { controlBase, describedBy } from './FieldFrame';
import { cx } from '../lib/cx';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
}

/**
 * A native select. On a phone that opens the platform picker, which is a
 * bigger, more familiar target than anything we could draw.
 */
export default function Select({
  id,
  label,
  hint,
  error,
  options,
  placeholder,
  className,
  ...rest
}: SelectProps) {
  return (
    <FieldFrame id={id} label={label} hint={hint} error={error}>
      <div className="relative">
        <select
          id={id}
          aria-describedby={describedBy(id, hint, error)}
          aria-invalid={error ? true : undefined}
          className={cx(
            controlBase,
            'appearance-none pr-12',
            error ? 'border-alert' : 'border-grey-300',
            className,
          )}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <svg
          aria-hidden="true"
          viewBox="0 0 12 8"
          className="pointer-events-none absolute top-1/2 right-4 h-2 w-3 -translate-y-1/2 fill-grey-600"
        >
          <path d="M0 0h12L6 8z" />
        </svg>
      </div>
    </FieldFrame>
  );
}
